import { NgClass } from "@angular/common";
import { Component, DestroyRef, OnInit, ViewChild, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Router, RouterLink, RouterLinkActive, NavigationEnd } from "@angular/router";
import { IconModule, slideDownUp } from "@crm-workspace/common-lib";
import { Store } from "@ngrx/store";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { NgScrollbarModule } from "ngx-scrollbar";
import { merge, of } from "rxjs";
import { filter, switchMap } from "rxjs/operators";
import { AuthService } from "../core/services/auth/auth.service";
import { environment } from "../../environments/environment";
import { isAdminOrManagerRoleName } from "../core/services/workspace-member/workspace-member.util";
import { CrmDashboardsService, type CrmDashboard } from "../modules/custom-dashboards/services/crm-dashboards.service";
import { CreateDashboardDialogComponent } from "../modules/custom-dashboards/components/create-dashboard-dialog/create-dashboard-dialog.component";
import { WorkspaceSwitcherComponent } from "../shared/components/workspace-switcher/workspace-switcher.component";
import { hasActivePaidSubscription } from "../modules/subscription/billing-subscription.models";
import { BillingSubscriptionApiService } from "../modules/subscription/services/billing-subscription-api.service";
import { BillingEntitlementsStateService } from "../modules/subscription/services/billing-entitlements-state.service";
import { LeadService } from "../modules/crm/pages/leads/lead.service";
import { ChangelogService } from "../modules/changelog/changelog.service";

@Component({
  selector: "sidebar",
  templateUrl: "./sidebar.html",
  standalone: true,
  animations: [slideDownUp],
  imports: [
    NgClass,
    RouterLink,
    NgScrollbarModule,
    RouterLinkActive,
    TranslateModule,
    IconModule,
    WorkspaceSwitcherComponent,
    CreateDashboardDialogComponent,
  ],
})
export class SidebarComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly auth = inject(AuthService);
  private readonly billingSubscriptionApi = inject(BillingSubscriptionApiService);
  private readonly crmDashboardsService = inject(CrmDashboardsService);
  readonly entitlementsState = inject(BillingEntitlementsStateService);
  readonly changelogService = inject(ChangelogService);

  @ViewChild(WorkspaceSwitcherComponent)
  private workspaceSwitcher?: WorkspaceSwitcherComponent;

  active = false;
  store: any;
  activeDropdown: string[] = [];
  parentDropdown: string = "";
  isAdminOrManager = false;
  showCreateDashboardDialog = false;
  readonly customDashboards = signal<CrmDashboard[]>([]);
  readonly subscriptionMenuLink = signal("/subscription");
  readonly hasPaidSubscription = signal(false);
  referenceVerificationEnabled = false;
  private readonly leadService = inject(LeadService);

  constructor(
    public translate: TranslateService,
    public storeData: Store<any>,
    public router: Router
  ) {
    this.initStore();
  }
  async initStore() {
    this.storeData
      .select((d) => d.index)
      .subscribe((d) => {
        this.store = d;
      });
  }

  ngOnInit() {
    this.setActiveDropdown();
    merge(
      of(undefined).pipe(switchMap(() => this.auth.getProfileMe({ bustCache: true }))),
      this.auth.profileMePushed$,
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (me) => {
          const roleRaw = me.role;
          const roleLower =
            typeof roleRaw === "string"
              ? roleRaw.trim().toLowerCase()
              : String(
                  (roleRaw as { name?: string } | null)?.name ?? "",
                ).trim().toLowerCase();
          this.isAdminOrManager = isAdminOrManagerRoleName(roleLower);
          this.loadCustomDashboards();
        },
        error: () => {
          /* keep defaults */
        },
      });

    this.loadSubscriptionMenuLink();
    this.loadReferenceVerificationAccess();
    void this.changelogService.refreshUnseenState();
    if (this.router.url.includes("/crm/references") && !this.activeDropdown.includes("project-management")) {
      this.activeDropdown = [...this.activeDropdown, "project-management"];
    }

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        filter(
          (event) =>
            event.urlAfterRedirects.startsWith("/plan-bills") ||
            event.urlAfterRedirects.startsWith("/subscription"),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.loadSubscriptionMenuLink();
      });

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        filter(
          (event) =>
            event.urlAfterRedirects.startsWith("/dashboards") ||
            event.urlAfterRedirects.startsWith("/dashboard"),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.loadCustomDashboards();
      });
  }

  private loadReferenceVerificationAccess(): void {
    this.leadService.getEnabledWorkspaceFeatures().subscribe({
      next: (res) => {
        this.referenceVerificationEnabled = (res.features ?? []).some(
          (feature) => feature.key === "reference_verification",
        );
      },
      error: () => {
        this.referenceVerificationEnabled = false;
      },
    });
  }

  private loadSubscriptionMenuLink(): void {
    this.billingSubscriptionApi.getSubscription().subscribe({
      next: (subscription) => {
        const subscribed = hasActivePaidSubscription(subscription);
        this.hasPaidSubscription.set(subscribed);
        this.subscriptionMenuLink.set(
          subscribed ? "/plan-bills/overview" : "/subscription",
        );
      },
      error: () => {
        /* keep default subscription catalogue link */
      },
    });
  }

  isSubscriptionNavActive(): boolean {
    const url = this.router.url.split("?")[0] ?? "";
    if (this.hasPaidSubscription()) {
      return url.startsWith("/plan-bills") || url.startsWith("/subscription");
    }
    return url.startsWith("/subscription");
  }

  setActiveDropdown() {
    const selector = document.querySelector(
      '.sidebar ul a[routerLink="' + window.location.pathname + '"]'
    );
    if (selector) {
      selector.classList.add("active");
      const ul: any = selector.closest("ul.sub-menu");
      if (ul) {
        let ele: any =
          ul.closest("li.menu").querySelectorAll(".nav-link") || [];
        if (ele.length) {
          ele = ele[0];
          setTimeout(() => {
            ele.click();
          });
        }
      }
    }
  }

  toggleMobileMenu() {
    if (window.innerWidth < 1024) {
      this.storeData.dispatch({ type: "toggleSidebar" });
    }
  }

  /** Collapse/expand rail — always close the workspace menu first. */
  onCollapseSidebar(): void {
    this.workspaceSwitcher?.closeMenu();
    this.storeData.dispatch({ type: "toggleSidebar" });
  }

  /**
   * Mini-rail hover: when the pointer leaves the sidebar (including the
   * overflowing workspace panel), close the workspace dropdown.
   */
  onSidebarMouseLeave(event: MouseEvent): void {
    const related = event.relatedTarget as Node | null;
    const sidebarEl = event.currentTarget as HTMLElement | null;
    if (related && sidebarEl?.contains(related)) {
      return;
    }
    this.workspaceSwitcher?.closeMenu();
  }

  toggleAccordion(name: string, parent?: string) {
    if (this.activeDropdown.includes(name)) {
      this.activeDropdown = this.activeDropdown.filter((d) => d !== name);
    } else {
      this.activeDropdown.push(name);
    }
  }

  isNavFeatureLocked(featureKey: string): boolean {
    if (this.entitlementsState.isLoading() || !this.entitlementsState.entitlements()) {
      return false;
    }
    return !this.entitlementsState.isFeatureIncluded(featureKey);
  }

  get showCustomDashboardNav(): boolean {
    return this.isAdminOrManager;
  }

  loadCustomDashboards(): void {
    if (
      !this.isAdminOrManager ||
      this.isNavFeatureLocked('Data & Reports:crm-dashboards')
    ) {
      this.customDashboards.set([]);
      return;
    }
    this.crmDashboardsService.listDashboards().subscribe({
      next: (dashboards) => this.customDashboards.set(dashboards),
      error: () => this.customDashboards.set([]),
    });
  }

  openCreateDashboardDialog(): void {
    if (this.isNavFeatureLocked('Data & Reports:crm-dashboards')) {
      void this.router.navigate(['/subscription/locked'], {
        queryParams: { feature: 'Data & Reports:crm-dashboards' },
      });
      return;
    }
    this.showCreateDashboardDialog = true;
  }
}
